import type { History } from "history";

// Each deploy renames the hashed page files, and the host keeps only the newest ones.
// A tab that stays open for days then asks for a page file that is gone (J/4065).
// This module checks in the background if the tab is old. When an old tab changes
// page, the browser loads the new page in full. The page the user works on never reloads.

const CHECK_INTERVAL_MS = 5 * 60_000;
const SHOWN_CHECK_GAP_MS = 60_000;
const FETCH_TIMEOUT_MS = 10_000;
const RELOAD_GAP_MS = 30_000;
const RELOAD_KEY = "stale-build:reloaded-at";

// How long a reload that the guard asked for is expected to take.
export const RELOAD_WAIT_MS = 10_000;
let reloadAskedAt: number | null = null;

// True for a short time after the guard asked the browser for a full load. The router
// still starts the new page first, and its file is gone. The error screen asks this flag,
// so it does not show and report a page that is about to be replaced.
export const isReloadingForNewBuild = (): boolean => {
  if (reloadAskedAt === null) return false;
  const elapsed = Date.now() - reloadAskedAt;
  return elapsed >= 0 && elapsed < RELOAD_WAIT_MS;
};

const SCRIPT_TAG = /<script\b[^>]*>/gi;
const MODULE_TYPE = /\stype=["']module["']/i;
const SRC_ATTRIBUTE = /\ssrc=["']([^"']*)["']/i;
const ENTRY_URL = /\/assets\/(index-[\w-]+\.js)$/;

const entryFromSrc = (src: string | null | undefined): string | null => src?.match(ENTRY_URL)?.[1] ?? null;

// Reads the main script name, such as "index-9c0be83f.js", from the built home page.
// Preload links and style sheets do not count. Returns null when there is no main script.
export const entryFromHtml = (html: string): string | null => {
  for (const [tag] of html.matchAll(SCRIPT_TAG)) {
    if (!MODULE_TYPE.test(tag)) continue;
    const entry = entryFromSrc(tag.match(SRC_ATTRIBUTE)?.[1]);
    if (entry) return entry;
  }
  return null;
};

// The main script of the page that runs now. The dev server has no hashed name: returns null.
export const runningEntry = (doc: Document): string | null => {
  for (const script of doc.querySelectorAll('script[type="module"][src]')) {
    const entry = entryFromSrc(script.getAttribute("src"));
    if (entry) return entry;
  }
  return null;
};

// The main script name of the build that the host serves now. Never throws.
// Returns null when the answer is missing, late, not HTML, or has no main script.
export const fetchLiveEntry = async (
  fetchImpl: typeof fetch = fetch,
  origin: string = window.location.origin,
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<string | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(`${origin}/`, { cache: "no-store", signal: controller.signal });
    if (!response.ok || !/text\/html/i.test(response.headers.get("content-type") ?? "")) return null;
    return entryFromHtml(await response.text());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

type StaleBuildGuardOptions = {
  history: Pick<History, "listen" | "location">;
  // The main script of this tab. When it is null, the guard does nothing.
  running: string | null;
  fetchLive: () => Promise<string | null>;
  reload: () => void;
  storage?: Pick<Storage, "getItem" | "setItem">;
  document?: Pick<Document, "hidden" | "addEventListener" | "removeEventListener">;
  intervalMs?: number;
};

// Storage throws in a private window or when site data is blocked.
const sessionStore = (): Pick<Storage, "getItem" | "setItem"> | undefined => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

// Returns a function that stops the guard.
export const startStaleBuildGuard = ({
  history,
  running,
  fetchLive,
  reload,
  storage,
  document: doc = document,
  intervalMs = CHECK_INTERVAL_MS,
}: StaleBuildGuardOptions): (() => void) => {
  if (running === null) return () => {};

  const store = storage ?? sessionStore();
  let stale = false;
  let checking = false;
  let lastCheckAt = Date.now();
  let pathname = history.location.pathname;

  const check = async () => {
    if (checking || doc.hidden) return;
    checking = true;
    lastCheckAt = Date.now();
    try {
      const live = await fetchLive();
      // A failed check keeps the last known value.
      if (live !== null) stale = live !== running;
    } catch {
      // Keep the last known value.
    } finally {
      checking = false;
    }
  };

  // The tab reloads itself after a deploy, and a host can show two builds at once.
  // The reload time is in the tab storage, because a reload clears the memory.
  // When storage fails, the reload still happens, but with no limit.
  const reloadedRecently = (): boolean => {
    try {
      const elapsed = Date.now() - Number(store?.getItem(RELOAD_KEY));
      return elapsed >= 0 && elapsed < RELOAD_GAP_MS;
    } catch {
      return false;
    }
  };
  const rememberReload = () => {
    try {
      store?.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // The reload still happens.
    }
  };

  const onVisibilityChange = () => {
    if (!doc.hidden && Date.now() - lastCheckAt > SHOWN_CHECK_GAP_MS) void check();
  };

  // The history listeners run after the address has changed. A reload here loads the
  // page that the user clicked, and it adds no history entry.
  const stopListening = history.listen((location) => {
    const pathnameChanged = location.pathname !== pathname;
    pathname = location.pathname;
    if (!pathnameChanged || !stale || reloadedRecently()) return;
    rememberReload();
    reloadAskedAt = Date.now();
    reload();
  });

  const timer = setInterval(() => void check(), intervalMs);
  doc.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    clearInterval(timer);
    doc.removeEventListener("visibilitychange", onVisibilityChange);
    stopListening();
    reloadAskedAt = null;
  };
};
